import { useGetBotAggregatedPositions } from "@/api/positions/positions-aggregated-bot";
import { useWallet } from "@/providers/wallet";
import { Trend, Value, WinRate } from "@/ui/formats";
import { IconComponent } from "@/ui/icon";
import LoadingIndicator from "@/ui/loading-indicator";
import Table from "@/ui/table";
import { displayWalletAddress } from "@/utils/wallet";

const WalletPositionsData = () => {
  const { userId, currentWallet } = useWallet();

  const {
    data: totalPnl,
    isLoading,
    isError,
  } = useGetBotAggregatedPositions(userId, currentWallet);

  return (
    <>
      {isLoading ? (
        <Table.Row>
          <Table.Column className="p-0 h-[1.875rem] align-top" colSpan={10}>
            <LoadingIndicator />
          </Table.Column>
        </Table.Row>
      ) : isError ? (
        <Table.Row>
          <Table.Column colSpan={10} className="py-[0.425rem] px-[2.1rem]">
            Something went wrong while loading your wallet positions. Please try
            again later.
          </Table.Column>
        </Table.Row>
      ) : !totalPnl ? (
        <Table.Row>
          <Table.Column colSpan={10} className="py-[0.425rem] px-[2.1rem]">
            No data available
          </Table.Column>
        </Table.Row>
      ) : (
        <Table.Row className="h-[1.875rem] relative cursor-auto border-none">
          <Table.Column className="h-[1.875rem] flex items-center gap-x-[0.5rem] pl-[2.1rem]">
            <IconComponent
              name={currentWallet.walletType}
              className="w-[0.9375rem] h-[0.9375rem] shrink-0"
            />
            <span>{displayWalletAddress(totalPnl.wallet)}</span>
          </Table.Column>
          <Table.Column>
            <Value value={totalPnl.totalAmountInvestUsd} withSign={false} />
          </Table.Column>
          <Table.Column>{totalPnl.trades}</Table.Column>
          <Table.Column>
            <WinRate value={totalPnl.winRate} />
          </Table.Column>
          <Table.Column>
            <Value
              value={totalPnl.realizedPnl}
              withSign={false}
              positiveNegativeColor
              withTrendArrow
            />
          </Table.Column>
          <Table.Column>
            <Trend
              value={totalPnl.realizedRoi}
              withSign={false}
              withTrendArrow
            />
          </Table.Column>
          <Table.Column>
            <Value
              value={totalPnl.unrealizedPnl}
              withSign={false}
              positiveNegativeColor
              withTrendArrow
            />
          </Table.Column>
          <Table.Column>
            <Trend
              value={totalPnl.unrealizedRoi}
              withSign={false}
              withTrendArrow
            />
          </Table.Column>
          <Table.Column>
            <Value
              value={totalPnl.netPnl}
              withSign={false}
              positiveNegativeColor
              withTrendArrow
            />
          </Table.Column>
          <Table.Column>
            <Trend value={totalPnl.netRoi} withSign={false} withTrendArrow />
          </Table.Column>
        </Table.Row>
      )}
    </>
  );
};

const WalletPositions = () => {
  const { currentWallet } = useWallet();

  return (
    <Table className="text-left text-xs">
      <colgroup>
        <col style={{ width: "16.5%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
        <col style={{ width: "9.155%" }} />
      </colgroup>
      <Table.Header className="h-[1.5625rem] bg-black">
        <Table.HeaderColumn className="pl-[2.1rem]">
          <div className="flex gap-1 items-center">
            Wallet
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Invested
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Trades
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Win Rate
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Realized PNL
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Realized ROI
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Unrealized PNL
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Unrealized ROI
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Net PNL
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div className="flex gap-1 items-center">
            Net ROI
            <div className="w-[0.75rem] inline-block h-[0.75rem] items-center justify-start" />
          </div>
        </Table.HeaderColumn>
      </Table.Header>
      <Table.Body>
        {!currentWallet ? (
          <Table.Row>
            <Table.Column colSpan={10} className="py-[0.425rem] px-[2.1rem]">
              Sign in to view data
            </Table.Column>
          </Table.Row>
        ) : (
          <WalletPositionsData />
        )}
      </Table.Body>
    </Table>
  );
};

export default WalletPositions;
